import { useEffect, useRef } from "react";
import { useQueryClient } from "react-query";

import { subscribeAgentEvents, reloadTargeting } from "../../copilot/agentEvents";

interface CopilotSyncRefetchers {
  refetchCampaign: () => Promise<unknown>;
  refetchAudiences: () => void;
  refetchCampaignAudiences: () => void;
  refetchVariables: () => void;
  refetchCampaignVariables: () => void;
}

interface AgentEvent {
  name: string;
  payload?: { campaignId?: string; kinds?: string[] | "all" };
  senderId?: string | null;
}

const DEBOUNCE_MS = 1500;

/**
 * Refetch-based copilot sync (client-copilot-v2): when the client's chat agent
 * changes this campaign through the Oppizi MCP, re-pull whatever it touched so
 * the wizard reflects the change live — no manual refresh. Mounted once inside
 * CampaignDataProvider (which owns every refetcher). Events arrive from the
 * copilot chat via the agentEvents bridge; bursts (an agent turn often makes
 * several writes) coalesce into one refetch per window. Events for a different
 * campaign are ignored; the interim kinds:"all" events (no campaignId) refetch
 * everything for the open campaign — the queries are the same cheap ones the
 * builder already runs.
 */
export const useCopilotSync = (campaignId: string | null, refetchers: CopilotSyncRefetchers): void => {
  const queryClient = useQueryClient();

  // Refs so the subscription survives re-renders without resubscribing and the
  // debounce timer always applies the freshest refetchers/campaignId.
  const stateRef = useRef({ campaignId, refetchers });
  stateRef.current = { campaignId, refetchers };
  const pendingKinds = useRef<Set<string>>(new Set());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const flush = () => {
      timer.current = null;
      const kinds = pendingKinds.current;
      pendingKinds.current = new Set();
      const { campaignId: id, refetchers: r } = stateRef.current;
      if (!id) return;
      if (kinds.has("details") || kinds.has("status") || kinds.has("design")) {
        void r.refetchCampaign();
      }
      if (kinds.has("audiences")) {
        r.refetchAudiences();
        r.refetchCampaignAudiences();
      }
      if (kinds.has("variables")) {
        r.refetchVariables();
        r.refetchCampaignVariables();
      }
      if (kinds.has("targeting")) {
        // Covers the not-yet-mounted Targeting step (stale cache) …
        void queryClient.invalidateQueries(["targeting-saved", id]);
        // … and the mounted one, whose one-shot rehydrate needs an explicit
        // reload (skipped while the user's own save queue is draining).
        reloadTargeting(id);
      }
      if (kinds.size > 0) {
        // Any write can move the price; the key prefix covers per-city entries.
        void queryClient.invalidateQueries(["channelCampaignCost", id]);
      }
    };

    return subscribeAgentEvents((event: AgentEvent) => {
      if (event.name !== "campaign_updated") return;
      const { campaignId: id } = stateRef.current;
      if (!id) return;
      // Guard only when the event names a campaign (0.5.0 payloads); interim
      // kinds:"all" events carry none and apply to the open campaign.
      if (event.payload?.campaignId && event.payload.campaignId !== id) return;
      const kinds = event.payload?.kinds;
      for (const kind of kinds === "all" || kinds === undefined
        ? ["details", "status", "design", "audiences", "variables", "targeting"]
        : kinds) {
        pendingKinds.current.add(kind);
      }
      if (!timer.current) timer.current = setTimeout(flush, DEBOUNCE_MS);
    });
    // queryClient is stable; everything mutable flows through stateRef.
  }, [queryClient]);

  // Drop any pending flush on unmount.
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );
};
